import { getProject } from 'sdk';
import { labelsCanonicalOptions } from '~/modules/label/query';
import { findProjectByIdOrSlug, projectQueryOptions } from '~/modules/project/query';
import { resetTaskInteraction } from '~/modules/task/helpers/board-helpers';
import { tasksCanonicalOptions } from '~/modules/task/query';
import { resolveChannelBySlug } from '~/query/basic/resolve-channel-by-slug';
import { cacheRestored, queryClient } from '~/query/query-client';

type ProjectRouteBeforeLoadArgs = {
  params: { tenantId: string; slug: string };
  context: { organization: { id: string } };
};

/** beforeLoad of the project route: resolves the project by slug or ID, seeds caches and rewrites the URL to the slug. */
export const projectRouteBeforeLoad = async ({ params, context }: ProjectRouteBeforeLoadArgs) => {
  // onLeave does not fire when only params change, so the reset is keyed by project; search-param-only navigations keep the selection.
  resetTaskInteraction(`project:${params.tenantId}:${params.slug}`);

  const { slug, tenantId } = params;
  const organizationId = context.organization.id;

  const projectData = await resolveChannelBySlug({
    idOrSlug: slug,
    tenantId,
    findInCache: findProjectByIdOrSlug,
    detailQueryOptions: (id) => projectQueryOptions(id, organizationId, tenantId),
    fetchBySlug: () => getProject({ path: { id: slug, organizationId, tenantId }, query: { slug: true } }),
    params,
    buildSlugOverrides: (entity) => ({ slug: entity.slug }),
  });

  // Fire-and-forget warm-up so the board and its labels panel don't waterfall on mount. Awaiting cache restore first lets
  // an already-synced list make these prefetches no-ops; without it a reload sees an empty cache and refetches everything.
  await cacheRestored;
  queryClient.prefetchQuery(tasksCanonicalOptions({ organizationId, tenantId, projectId: projectData.id }));
  queryClient.prefetchQuery(labelsCanonicalOptions({ organizationId, tenantId, projectId: projectData.id }));

  return { project: projectData };
};
